import { useCallback } from "react";
import api from "@/utils/axios-utils";
import { useAuthStore } from "@/store/useAuthStore";
import {
  useSpaceStore,
  type AiMode,
  type CollabMode,
  type SpaceParticipant,
  type WriteMode,
} from "../store/useSpaceStore";

export type CreateSpaceResult = {
  spaceId: string;
  spaceName: string;
};

export function useSpace() {
  // Use getState() so this hook never subscribes to store changes —
  // it only dispatches actions and has no state of its own.
  const s = () => useSpaceStore.getState();

  // When acting as a teacher, forward the teacher id so the gateway and backend
  // can resolve the correct teacher identity for all direct fetch() calls.
  const getActAsHeader = (): Record<string, string> => {
    const id = useAuthStore.getState().actingTeacherId;
    return id ? { "X-Act-As-Teacher-Id": String(id) } : {};
  };

  // Creates a space and stores it in SpaceStore so subsequent calls can use spaceId.
  async function createSpace(
    classroomIds: number[],
    lessonIds: number[],
    lessonContext?: string,
    spaceName?: string,
    draft?: boolean,
    description?: string,
  ): Promise<CreateSpaceResult> {
    const res = await api.post("/spaces", {
      classroomIds,
      lessonIds,
      lessonContext: lessonContext?.trim() || null,
      spaceName: spaceName?.trim() || null,
      description: description?.trim() || null,
      draft: draft ?? false,
    });
    s().setSpace(
      res.data.spaceId,
      res.data.spaceName,
      lessonContext?.trim() || null,
    );
    return res.data as CreateSpaceResult;
  }

  async function closeSpace(spaceId: string): Promise<void> {
    // Call gateway POST /close: broadcasts space_closed to all ctrl connections,
    // saves final snapshot, removes space from registry, then proxies to backend.
    const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
    const token = useAuthStore.getState().token;
    const res = await fetch(`${spacesBase}/spaces/${spaceId}/close`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token ?? ""}`, ...getActAsHeader() },
    });
    if (!res.ok) throw new Error(`Gateway delete failed: ${res.status}`);
    s().clearSpace();
  }

  async function updateSettings(
    spaceId: string,
    { aiMode, collabMode }: { aiMode?: AiMode; collabMode?: CollabMode },
  ): Promise<void> {
    // Route through gateway so it can broadcast ai_mode_changed / collab_mode_changed
    const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
    const token = useAuthStore.getState().token;
    if (!token) throw new Error("unauthenticated");
    const body: Record<string, unknown> = {};
    if (aiMode !== undefined) body.aiMode = aiMode;
    if (collabMode !== undefined) body.collabMode = collabMode;
    const res = await fetch(`${spacesBase}/spaces/${spaceId}/settings`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...getActAsHeader(),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok)
      throw new Error(`Gateway settings update failed: ${res.status}`);
    if (aiMode !== undefined) s().setAiMode(aiMode);
    if (collabMode !== undefined) s().setCollabMode(collabMode);
  }

  async function updateParticipantWriteMode(
    spaceId: string,
    userId: number,
    writeMode: WriteMode,
  ): Promise<void> {
    // Optimistic update — move slider immediately, revert on failure
    const prev = s().participants.find((p) => p.userId === userId)?.writeMode;
    s().updateParticipantWriteMode(userId, writeMode);

    // Single call to gateway notify: DO updates writeModes map, persists to DB,
    // and broadcasts write_mode_changed to the student over ctrl WS.
    const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
    const token = useAuthStore.getState().token;
    if (!token) {
      if (prev !== undefined) s().updateParticipantWriteMode(userId, prev);
      throw new Error("unauthenticated");
    }
    try {
      const res = await fetch(
        `${spacesBase}/spaces/${spaceId}/participants/${userId}/notify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...getActAsHeader(),
          },
          body: JSON.stringify({ writeMode }),
        },
      );
      if (!res.ok) throw new Error(`notify failed: ${res.status}`);
    } catch (err) {
      if (prev !== undefined) s().updateParticipantWriteMode(userId, prev);
      throw err;
    }
  }

  // Backend sends `writeMode` and `classrooms` directly on the participant row.
  type ParticipantApiResponse = Omit<
    SpaceParticipant,
    "joinedAt" | "lastJoinedAt" | "classrooms"
  > & {
    joinedAt?: string | null;
    lastJoinedAt?: string | null;
    classrooms?: { id: number; name: string }[];
  };

  // Loads the enrolled participant list from DB into SpaceStore.
  const fetchParticipants = useCallback(
    async (spaceId: string): Promise<void> => {
      const res = await api.get<ParticipantApiResponse[]>(
        `/spaces/${spaceId}/participants`,
      );
      s().setParticipants(
        res.data.map((p) => ({
          ...p,
          joinedAt: p.joinedAt ? new Date(p.joinedAt) : undefined,
          lastJoinedAt: p.lastJoinedAt ? new Date(p.lastJoinedAt) : undefined,
          classrooms: p.classrooms ?? [],
        })),
      );
    },
    [],
  );

  // Returns userIds of users currently connected to the DO (live WebSocket).
  const fetchConnected = useCallback(
    async (spaceId: string): Promise<string[]> => {
      const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
      const token = useAuthStore.getState().token;
      const res = await fetch(`${spacesBase}/spaces/${spaceId}/connected`, {
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          ...getActAsHeader(),
        },
      });
      if (!res.ok) return [];
      const data = (await res.json()) as { userIds: string[] };
      return data.userIds;
    },
    [],
  );

  // Tells the DO to persist its current TLDraw sync-document to the DB. The DO
  // owns the snapshot — the frontend doesn't send any data.
  async function saveSnapshot(spaceId: string): Promise<void> {
    const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
    const token = useAuthStore.getState().token;
    await fetch(`${spacesBase}/spaces/${spaceId}/snapshot/save`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token ?? ""}`, ...getActAsHeader() },
    }).catch(() => {});
  }

  async function pauseSpace(spaceId: string): Promise<void> {
    const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
    const token = useAuthStore.getState().token;
    const res = await fetch(`${spacesBase}/spaces/${spaceId}/pause`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token ?? ""}`, ...getActAsHeader() },
    });
    if (!res.ok) throw new Error(`Gateway pause failed: ${res.status}`);
  }

  async function resumeSpace(spaceId: string): Promise<void> {
    const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
    const token = useAuthStore.getState().token;
    const res = await fetch(`${spacesBase}/spaces/${spaceId}/resume`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token ?? ""}`, ...getActAsHeader() },
    });
    if (!res.ok) throw new Error(`Gateway resume failed: ${res.status}`);
  }

  // Transitions a draft space to open status so students can join.
  async function publishSpace(spaceId: string): Promise<void> {
    await api.post(`/spaces/${spaceId}/publish`);
  }

  // Reopens a closed space — sets status back to open. No gateway call needed
  // since the DO is already dead; a new one will spin up on first WS connection.
  async function relaunchSpace(spaceId: string): Promise<void> {
    await api.post(`/spaces/${spaceId}/relaunch`);
  }

  // Permanently deletes a draft space from the DB (draft only).
  async function deleteSpace(spaceId: string): Promise<void> {
    await api.delete(`/spaces/${spaceId}`);
  }

  return {
    createSpace,
    closeSpace,
    updateSettings,
    updateParticipantWriteMode,
    fetchParticipants,
    fetchConnected,
    saveSnapshot,
    pauseSpace,
    resumeSpace,
    publishSpace,
    relaunchSpace,
    deleteSpace,
  };
}
